import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import api from "../services/api";
import { getMe, getClubs, type MeResponse } from "../services/api";
import { type Club, type ClubAccess, type ClubCapabilities, type ClubFeatureFlag } from "../services/clubs";
import { registerPushNotifications, unregisterPushNotifications } from "../services/capacitor";
import {
  AWAY_REFRESH_MS,
  SESSION_ENDED_EVENT,
  clearActivity,
  isIdleExpired,
  awayFor,
  touchActivity,
  type SessionEndReason,
} from "../lib/sessionActivity";

type User = {
  id: string;
  email: string;
  name?: string | null;
  full_name?: string | null;
  locale?: string;
  avatar_url?: string | null;
  profile_url?: string | null;
  phone?: string | null;
  date_of_birth?: string | null;
};

type AthleteProfile = {
  guardian_contact: { name?: string; email?: string; phone?: string; relationship?: string } | null;
  has_guardian: boolean;
};

type DemoLimits = {
  isDemo: boolean;
  // null = no cap on this tier. The trial no longer caps head-count: what runs
  // out is the time, not the roster.
  limits: { MAX_CLUBS: number | null; MAX_COACHES: number | null; MAX_ATHLETES: number | null };
  current: { clubs: number; coaches: number; athletes: number };
} | null;

type UserStatus = "active" | "demo" | "inactive";

type Impersonator = { id: string; email: string; full_name: string | null };

type AuthState = {
  user: User | null;
  token: string | null;
  roles: { id: string; code: string; scope_type: "platform" | "club" | "team"; scope_id: string | null }[];
  clubs: Club[];
  activeRole: string | null;
  activeClubId: string | null;
  // For a parent viewing several children: the currently selected child
  // (athlete user id). Persisted so the choice survives navigation between
  // parent pages, not just within one page. Null = default to the first child.
  activeChildId: string | null;
  // Whether the currently selected child is "individual" (no real club → no
  // trainings/events/attendance/metrics). Drives the parent bottom nav + home.
  // Transient: recomputed by the parent screens when children load.
  activeChildIndividual: boolean;
  athleteProfile: AthleteProfile | null;
  userStatus: UserStatus | null;
  demoExpiresAt: string | null;
  demoLimits: DemoLimits;
  // The platform_admin whose session minted the current token via
  // /auth/impersonate. Null in normal sessions.
  impersonator: Impersonator | null;

  loading: boolean;
  setAuth: (u: User, t: string, remember?: boolean) => Promise<void>;
  setActiveClubId: (id: string | null) => void;
  setActiveRole: (role: string | null) => void;
  setActiveChildId: (id: string | null) => void;
  setActiveChildIndividual: (v: boolean) => void;
  refresh: () => Promise<void>;
  logout: () => void;
  // Swap the current admin token for an impersonation token. The
  // original admin token is stashed in a sibling cookie so
  // stopImpersonation can restore it.
  startImpersonation: (impersonationToken: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;

  // Tier capabilities of the active club (null for non-club users or until
  // loaded). `hasClubCapability` is the convenience gate used across the UI.
  clubCapabilities: ClubCapabilities | null;
  hasClubCapability: (flag: ClubFeatureFlag) => boolean;

  // Whether the active club uses per-sport coordinators. Off unless the club
  // ticked it in Setari, and the whole coordinator UI hangs off this.
  coordinatorsEnabled: boolean;

  // Where the active club stands with its trial. Null for someone with no
  // club. `clubLocked` is the single question most screens have.
  clubAccess: ClubAccess | null;
  clubLocked: boolean;
};

const ORIGINAL_TOKEN_COOKIE = "igrowth_original_token";
const TOKEN_KEY = "igrowth_token";
// Persists an explicit active-role choice for users with more than one role
// (e.g. club_admin who is also a coach), so the switch survives a refresh.
const ACTIVE_ROLE_KEY = "igrowth_active_role";
const ACTIVE_CHILD_KEY = "igrowth_active_child";
const ACTIVE_CLUB_KEY = "igrowth_active_club";

// Persist the session token in localStorage (which survives app restarts
// inside the Capacitor WebView) with a long-lived cookie as a backup, so
// the app "remembers" the login instead of asking for it every week. Reads
// prefer localStorage and fall back to the cookie to migrate existing
// logged-in users seamlessly.
function loadToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY) || Cookies.get(TOKEN_KEY) || null;
  } catch {
    return Cookies.get(TOKEN_KEY) || null;
  }
}
// `remember` (default true) keeps the login across browser restarts
// (localStorage + a 1-year cookie). When false — the "remember me" box is
// unticked — the token lives only for the browser session (sessionStorage +
// a session cookie) and is cleared when the browser is closed.
function saveToken(t: string, remember: boolean = true) {
  try {
    if (remember) {
      localStorage.setItem(TOKEN_KEY, t);
      sessionStorage.removeItem(TOKEN_KEY);
    } else {
      sessionStorage.setItem(TOKEN_KEY, t);
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch {
    /* private mode / storage disabled — cookie still covers it */
  }
  Cookies.set(TOKEN_KEY, t, remember ? { expires: 365 } : undefined);
}
function clearToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
  Cookies.remove(TOKEN_KEY);
}

const AuthCtx = createContext<AuthState>({} as any);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(loadToken());
  const [roles, setRoles] = useState<AuthState["roles"]>([]);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [activeRole, setActiveRole] = useState<string | null>(null);
  const [activeChildId, setActiveChildIdState] = useState<string | null>(() => {
    try { return localStorage.getItem(ACTIVE_CHILD_KEY); } catch { return null; }
  });
  const [activeChildIndividual, setActiveChildIndividual] = useState(false);
  const [activeClubId, setActiveClubIdState] = useState<string | null>(() => {
    try { return localStorage.getItem(ACTIVE_CLUB_KEY); } catch { return null; }
  });
  const setActiveClubId = (id: string | null) => {
    setActiveClubIdState(id);
    try {
      if (id) localStorage.setItem(ACTIVE_CLUB_KEY, id);
      else localStorage.removeItem(ACTIVE_CLUB_KEY);
    } catch { /* ignore */ }
  };
  const [athleteProfile, setAthleteProfile] = useState<AthleteProfile | null>(null);
  const [userStatus, setUserStatus] = useState<UserStatus | null>(null);
  const [demoExpiresAt, setDemoExpiresAt] = useState<string | null>(null);
  const [demoLimits, setDemoLimits] = useState<DemoLimits>(null);
  const [impersonator, setImpersonator] = useState<Impersonator | null>(null);
  // Start with loading=true if we have a token (prevents flash of wrong content during auth refresh)
  const [loading, setLoading] = useState<boolean>(!!loadToken());

  // Când se schimbă tokenul, setăm headerul Authorization pe axios
  useEffect(() => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
  }, [token]);

  // La montare / când token există: adu /auth/me și /clubs
  useEffect(() => {
    if (!token) return;
    (async () => {
      await refresh();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  /**
   * Ending a session, and saying why.
   *
   * One place does it, so every route out — an idle limit crossed, a token the
   * server refused — leaves the same state behind and shows the same sentence
   * on the login screen. The navigation is a hard one deliberately: a session
   * that is over must not leave a single cached page, list or form behind, and
   * React Router would keep all of them.
   */
  function endSession(reason: SessionEndReason) {
    logout();
    const here = window.location.pathname + window.location.search;
    const onPublicPage = /^\/(login|register|register-club|forgot-password|reset-password|join|setup|verify-email|privacy|terms)/.test(
      window.location.pathname
    );
    if (onPublicPage) return;
    const next = encodeURIComponent(here);
    window.location.replace(`/login?reason=${reason}&next=${next}`);
  }

  /**
   * Keep the clock honest, and act when it runs out.
   *
   * Three jobs, all hanging off whether there is a session at all:
   *   - record activity, shared across tabs, on the coarse signals that mean
   *     somebody is actually using the app (not mousemove — that fires
   *     hundreds of times a second and would write to storage as often);
   *   - when the tab comes back after a while, re-check: past the limit the
   *     session ends, inside it we refetch, because a page that sat in the
   *     background for an hour is showing yesterday's numbers;
   *   - a slow tick, so a tab left open and untouched signs itself out instead
   *     of waiting for the person to come back and find an empty app.
   */
  useEffect(() => {
    if (!token) return;

    // Somebody has a session and just loaded the app: that is activity.
    if (isIdleExpired()) {
      endSession("idle");
      return;
    }
    touchActivity(Date.now(), true);

    const events: (keyof WindowEventMap)[] = ["pointerdown", "keydown", "focus"];
    const onActivity = () => touchActivity();
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (isIdleExpired()) {
        endSession("idle");
        return;
      }
      const away = awayFor();
      touchActivity(Date.now(), true);
      // Back after a real absence: re-validate and reload what the app knows.
      // If the token has gone stale in the meantime, refresh() signs out —
      // which is the behaviour we want, rather than a shell with no data.
      if (away > AWAY_REFRESH_MS) void refresh();
    };
    document.addEventListener("visibilitychange", onVisible);

    const tick = window.setInterval(() => {
      if (isIdleExpired()) endSession("idle");
    }, 60_000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(tick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  /** A 401 from anywhere in the app ends the session — see api.ts. */
  useEffect(() => {
    const onEnded = (e: Event) => {
      if (!loadToken()) return; // already signed out; nothing to do
      // Borrowed sessions last an hour. When one runs out, the admin gets
      // their OWN session back rather than being signed out of the platform —
      // logout would also bin the stashed admin token and make them log in
      // again for somebody else's token expiring.
      if (Cookies.get(ORIGINAL_TOKEN_COOKIE)) {
        void stopImpersonation();
        return;
      }
      const reason = ((e as CustomEvent).detail?.reason as SessionEndReason) || "expired";
      endSession(reason);
    };
    window.addEventListener(SESSION_ENDED_EVENT, onEnded);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, onEnded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refresh() {
    if (!token) return;
    setLoading(true);
    try {
      const me: MeResponse = await getMe();
      setUser({
        id: me.user.id,
        email: me.user.email,
        name: me.user.full_name ?? null,
        full_name: me.user.full_name ?? null,
        locale: me.user.locale,
        avatar_url: me.user.avatar_url ?? null,
        profile_url: me.user.profile_url ?? null,
        phone: me.user.phone ?? null,
        date_of_birth: me.user.date_of_birth ?? null,
      });
      setRoles(me.roles);
      // Honor a user's explicit role choice (e.g. a club_admin who is also a
      // coach switched into the coach app) across refreshes, as long as they
      // still hold that role; otherwise fall back to the server's default.
      const storedRole = (() => { try { return localStorage.getItem(ACTIVE_ROLE_KEY); } catch { return null; } })();
      if (storedRole && me.roles.some((r) => r.code === storedRole)) {
        setActiveRole(storedRole);
      } else {
        setActiveRole(me.activeRole || null);
      }
      setAthleteProfile(me.athleteProfile ?? null);
      setUserStatus(me.user.status ?? "active");
      setDemoExpiresAt(me.user.demo_expires_at ?? null);
      setDemoLimits(me.demoLimits ?? null);
      setImpersonator(me.impersonator ?? null);

      const c = await getClubs();
      // /clubs knows the club rows; /auth/me knows what the club is ENTITLED to
      // — its tier capabilities and its trial state. Only the first list was
      // being kept, which left `capabilities` undefined everywhere and quietly
      // turned every tier-gated feature off, paid or not.
      const entitlements = new Map((me.clubs || []).map((x: any) => [x.id, x]));
      const list = c.map((x) => {
        const extra = entitlements.get(x.club.id) as any;
        return extra
          ? { ...x.club, capabilities: extra.capabilities, access: extra.access, trial_ends_at: extra.trial_ends_at }
          : x.club;
      });
      setClubs(list);

      // Prefer a remembered club if the user still administers it; otherwise the
      // current selection if still valid, else the first club.
      if (list.length > 0) {
        const stored = (() => { try { return localStorage.getItem(ACTIVE_CLUB_KEY); } catch { return null; } })();
        const validStored = stored && list.some((x) => x.id === stored) ? stored : null;
        const validCurrent = activeClubId && list.some((x) => x.id === activeClubId) ? activeClubId : null;
        setActiveClubId(validStored || validCurrent || list[0].id);
      }
    } catch (err: any) {
      console.error("[auth] refresh failed:", err);
      // Only a refused token ends the session. This used to sign out on ANY
      // failure, which meant a cold-starting server or two seconds of bad
      // signal threw the person out of a session that was perfectly valid —
      // and, worse, could leave them on a dashboard with nothing on it. A
      // transient failure now keeps the session: the 401 interceptor is what
      // decides a token is finished.
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }

  // setAuth este folosit după login/register
  async function setAuth(u: User, t: string, remember: boolean = true) {
    setToken(t);
    saveToken(t, remember);
    touchActivity(Date.now(), true);
    // setăm imediat headerul pentru ca următoarele calluri să fie autorizate
    api.defaults.headers.common["Authorization"] = `Bearer ${t}`;
    // setăm user minim (din răspunsul de login), apoi facem refresh ca să luăm roluri/cluburi
    setUser(u);
    await refresh();
    // Register for push notifications after successful auth
    registerPushNotifications();
  }

  // Set the active role and remember the choice so it survives a refresh.
  function setActiveChildId(id: string | null) {
    setActiveChildIdState(id);
    try {
      if (id) localStorage.setItem(ACTIVE_CHILD_KEY, id);
      else localStorage.removeItem(ACTIVE_CHILD_KEY);
    } catch { /* ignore */ }
  }

  function chooseActiveRole(role: string | null) {
    setActiveRole(role);
    try {
      if (role) localStorage.setItem(ACTIVE_ROLE_KEY, role);
      else localStorage.removeItem(ACTIVE_ROLE_KEY);
    } catch {
      /* storage disabled — the in-memory choice still applies for this session */
    }
  }

  function logout() {
    unregisterPushNotifications();
    clearToken();
    clearActivity();
    try { localStorage.removeItem(ACTIVE_ROLE_KEY); } catch { /* ignore */ }
    try { localStorage.removeItem(ACTIVE_CHILD_KEY); } catch { /* ignore */ }
    setActiveChildIdState(null);
    Cookies.remove(ORIGINAL_TOKEN_COOKIE);
    delete api.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setRoles([]);
    setClubs([]);
    setActiveRole(null);
    setActiveClubIdState(null);
    try { localStorage.removeItem(ACTIVE_CLUB_KEY); } catch { /* ignore */ }
    setAthleteProfile(null);
    setUserStatus(null);
    setDemoExpiresAt(null);
    setDemoLimits(null);
    setImpersonator(null);
  }

  /**
   * Swap the active session for one minted by /auth/impersonate. The
   * caller's real admin token is stashed in a sibling cookie so
   * `stopImpersonation` can restore it without forcing the admin to
   * log back in. We hard-reload after swap so React Router fully
   * rebuilds the tree against the impersonated user's roles.
   */
  async function startImpersonation(impersonationToken: string) {
    const currentToken = token || loadToken();
    if (!currentToken) throw new Error("No active session to impersonate from");
    Cookies.set(ORIGINAL_TOKEN_COOKIE, currentToken, { expires: 1 });
    saveToken(impersonationToken);
    // Force a full reload so every cached datum (axios defaults, React
    // Router state, react-query caches etc.) is rebuilt under the
    // impersonated user.
    window.location.href = "/dashboard";
  }

  /**
   * Restore the platform-admin session that was active before
   * `startImpersonation` ran. Falls back to a normal logout if the
   * stash is missing (e.g. user cleared cookies).
   */
  async function stopImpersonation() {
    const original = Cookies.get(ORIGINAL_TOKEN_COOKIE);
    if (!original) {
      logout();
      window.location.href = "/login";
      return;
    }
    saveToken(original);
    Cookies.remove(ORIGINAL_TOKEN_COOKIE);
    window.location.href = "/admin/users";
  }

  // Derivă rolul activ dacă nu e setat deja (fallback din rolurile disponibile)
  // Prioritatea: admin platformă > admin club > antrenor > atlet > părinte
  // Adminii trebuie să ajungă în dashboard, nu în app. „parent" e inclus ca să
  // nu cadă un cont exclusiv de părinte pe codes[0] (aliniat cu pickActiveRole
  // din backend, unde athlete are prioritate față de parent).
  const derivedActiveRole = useMemo(() => {
    if (activeRole) return activeRole;
    const codes = roles.map((r) => r.code);
    const prio = ["platform_admin", "club_admin", "coordinator", "coach", "athlete", "parent"];
    return prio.find((p) => codes.includes(p)) || codes[0] || null;
  }, [activeRole, roles]);

  const clubCapabilities = useMemo<ClubCapabilities | null>(() => {
    const c = clubs.find((cl) => cl.id === activeClubId) || clubs[0];
    return c?.capabilities ?? null;
  }, [clubs, activeClubId]);
  const hasClubCapability = (flag: ClubFeatureFlag): boolean => !!clubCapabilities?.flags?.[flag];

  const clubAccess = useMemo<ClubAccess | null>(() => {
    const c = clubs.find((cl) => cl.id === activeClubId) || clubs[0];
    return c?.access ?? null;
  }, [clubs, activeClubId]);
  // Default false: a missing state must never make a working club look locked.
  const clubLocked = clubAccess?.state === "locked";

  const coordinatorsEnabled = useMemo<boolean>(() => {
    const c = clubs.find((cl) => cl.id === activeClubId) || clubs[0];
    return !!c?.coordinators_enabled;
  }, [clubs, activeClubId]);

  const value: AuthState = {
    user,
    token,
    roles,
    clubs,
    activeRole: derivedActiveRole,
    activeClubId,
    activeChildId,
    activeChildIndividual,
    clubCapabilities,
    hasClubCapability,
    coordinatorsEnabled,
    clubAccess,
    clubLocked,
    athleteProfile,
    userStatus,
    demoExpiresAt,
    demoLimits,
    impersonator,
    loading,
    setAuth,
    setActiveClubId,
    setActiveRole: chooseActiveRole,
    setActiveChildId,
    setActiveChildIndividual,
    refresh,
    logout,
    startImpersonation,
    stopImpersonation,
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}
